import WritingDaily from '@/features/training/streaks/WritingDaily'
import RhymeStreak from '@/features/training/streaks/RhymeStreak'
import TrainingNavDropdown from '@/components/TrainingNavDropdown'
import { Link } from 'react-router-dom'
import { useAuth } from '@/features/auth/useAuth'
import { useFeatureFlag } from '@/features/feature-flags/useFeatureFlag'
import { TOOLS, ToolIcon } from '@/lib/tools'
import '@/features/dashboard/dashboard.css'

/**
 * Global topbar. Renders the dash-style chrome used by the dashboard and
 * the editor on every authenticated route. Auth-aware:
 *
 *  - Signed in:  ← Dashboard | logo | tool chips · gear
 *  - Signed out: wurds.org   |      | Sign in
 *
 * Layout decides whether to mount this; pages with their own shell
 * (/dashboard) or fully bespoke chrome (/login) opt out at the Layout
 * level rather than via a prop here.
 *
 * On mobile (≤900px) the *signed-in* topbar is hidden — the MobileBottomNav
 * takes over global navigation. We mark the shell with `--authed` so the
 * CSS rule can target that case without affecting the signed-out brand /
 * Sign in pill (which mobile users still need to access).
 */

function GearIcon() {
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" /></svg>
}

function BackArrowIcon() {
  return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
}

export default function AppTopbar() {
  const { user, loading } = useAuth()
  const flagValues = {
    'tools.rwg': useFeatureFlag('tools.rwg'),
    'dashboard.new-to-wexabet': useFeatureFlag('dashboard.new-to-wexabet'),
    'dashboard.training': useFeatureFlag('dashboard.training'),
  }
  const visibleTools = TOOLS.filter((t) => t.flag === null || flagValues[t.flag])

  // While auth bootstraps, paint a placeholder strip so we don't flash
  // between signed-out and signed-in layouts.
  if (loading) {
    return <div className="doc-topbar-shell" aria-hidden="true"><header className="dash-topbar" /></div>
  }

  // When signed in, mark the shell so mobile CSS can hide it — the bottom
  // nav takes over global navigation at ≤900px. Signed-out users keep the
  // shell (it carries the brand + Sign in pill).
  const shellClass = user ? 'doc-topbar-shell doc-topbar-shell--authed' : 'doc-topbar-shell'

  return (
    <div className={shellClass}>
      <header className="dash-topbar">
        <div className="doc-topbar-left">
          {user ? (
            <Link to="/dashboard" className="doc-topbar-back">
              <BackArrowIcon />
              <span>Dashboard</span>
            </Link>
          ) : (
            <Link to="/" className="doc-topbar-brand" aria-label="wurds.org home">
              <img src="/logo.svg" alt="wurds.org" />
            </Link>
          )}

          {user && (
            <Link to="/dashboard" className="doc-topbar-logo" aria-label="wurds.org">
              <img src="/logo.svg" alt="wurds.org" />
            </Link>
          )}
        </div>

        {user && <div className="streak-nav-slot"><RhymeStreak placement="navbar" /><WritingDaily placement="navbar" /></div>}
        <div className="dash-topbar-right">
          {user ? (
            <>
              {visibleTools.map((t) =>
                t.to === '/training' ? <TrainingNavDropdown key={t.name} /> : t.to ? (
                  <Link key={t.name} to={t.to} className="dash-tool-chip" style={{ '--chip-color': t.color }}>
                    <span className="dash-tool-chip-icon" style={{ background: t.color }}>
                      <ToolIcon kind={t.icon} />
                    </span>
                    <span>{t.name}</span>
                  </Link>
                ) : (
                  <a key={t.name} href={t.href} className="dash-tool-chip" style={{ '--chip-color': t.color }}>
                    <span className="dash-tool-chip-icon" style={{ background: t.color }}>
                      <ToolIcon kind={t.icon} />
                    </span>
                    <span>{t.name}</span>
                  </a>
                ),
              )}

              <Link to="/settings" className="dash-icon-btn" aria-label="Settings">
                <GearIcon />
              </Link>
            </>
          ) : (
            <Link to="/login" className="doc-topbar-signin">Sign in</Link>
          )}
        </div>
      </header>
    </div>
  )
}
